import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { Mail, Lock, User, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthShell, AuthCard, authInputClass } from '../components/Common/AuthShell';

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuthStore();
  const { t } = useTranslation('auth');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !password || !confirmPassword) {
      toast.error(t('common.fillAllFields'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('common.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('register.passwordTooShort'));
      return;
    }

    try {
      await register(email, password, name);
      toast.success(t('register.accountCreated'));
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || t('register.registrationFailed'));
    }
  };

  return (
    <AuthShell tagline={t('register.tagline')} back={{ label: t('common.backToHome'), to: '/' }}>
      <AuthCard>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name Input */}
          <div>
            <label htmlFor="name" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.fullName')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <User className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={authInputClass}
                placeholder={t('common.namePlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.emailAddress')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Mail className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInputClass}
                placeholder={t('common.emailPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.password')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={authInputClass}
                placeholder={t('common.passwordPlaceholder')}
                disabled={isLoading}
              />
            </div>
            <p className="mt-1.5 text-xs text-white/40">{t('register.passwordHint')}</p>
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.confirmPassword')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={authInputClass}
                placeholder={t('common.passwordPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={isLoading} className="landing-primary-cta w-full disabled:cursor-not-allowed disabled:opacity-60">
            {isLoading ? (
              <>
                <svg className="-ml-1 mr-1 h-5 w-5 animate-spin text-growth-ink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('register.submitting')}
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5" />
                {t('register.submit')}
              </>
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-transparent px-4 text-white/[0.5] [backdrop-filter:blur(8px)]">
                {t('register.haveAccount')}
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex w-full items-center justify-center rounded-2xl border border-white/14 bg-white/[0.06] px-4 py-3 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.1]"
            >
              {t('register.signInInstead')}
            </Link>
          </div>
        </div>
      </AuthCard>

      {/* Benefits Badges */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="glass-skin rounded-2xl p-3 text-center">
          <p className="text-xs font-bold text-lime-200">{t('register.benefits.freePlanLabel')}</p>
          <p className="mt-1 text-xs text-white/50">{t('register.benefits.freePlanDesc')}</p>
        </div>
        <div className="glass-skin rounded-2xl p-3 text-center">
          <p className="text-xs font-bold text-cyan-200">{t('register.benefits.easySetupLabel')}</p>
          <p className="mt-1 text-xs text-white/50">{t('register.benefits.easySetupDesc')}</p>
        </div>
        <div className="glass-skin rounded-2xl p-3 text-center">
          <p className="text-xs font-bold text-violet-200">{t('register.benefits.noCardLabel')}</p>
          <p className="mt-1 text-xs text-white/50">{t('register.benefits.noCardDesc')}</p>
        </div>
      </div>
    </AuthShell>
  );
};
